import React from 'react';
import Table from '../../components/ui/Table.jsx';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import { useApi } from '../../hooks/useApi.js';
import { apiFetch } from '../../api.js';
import { Link, RouteContext } from '../../router.jsx';

const initialForm = {
  username: '',
  firstName: '',
  lastName: '',
  email: '',
  adminPrivilege: false,
  chairPrivilege: false,
  editPrivilege: false,
  activated: true,
  ban: false
};

export default function StaffAdmins() {
  const { navigate } = React.useContext(RouteContext);
  const [refresh, setRefresh] = React.useState(0);
  const [query, setQuery] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const limit = 25;
  const path = `/api/admins?limit=${limit}&offset=${offset}&q=${encodeURIComponent(query)}`;
  const { data, loading, error } = useApi(path, [refresh, query, offset]);
  const [form, setForm] = React.useState(initialForm);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });
  const [showForm, setShowForm] = React.useState(false);

  const items = data?.items || [];
  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    { key: 'privileges', label: 'Privileges' }
  ];

  const tableData = items.map((admin) => ({
    name: <Link to={`/staff/admins/${admin.id}`}>{fullName(admin) || admin.username || '—'}</Link>,
    email: admin.email || '—',
    role: roleLabel(admin),
    privileges: renderPrivileges(admin)
  }));

  const updateField = (key) => (event) => {
    const value = ['adminPrivilege', 'chairPrivilege', 'editPrivilege', 'activated', 'ban'].includes(key)
      ? event.target.checked
      : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setMessage({ text: '', tone: 'info' });
  };

  const validate = () => {
    if (!form.username.trim()) return 'Username is required.';
    if (!form.email.trim()) return 'Email is required.';
    return '';
  };

  const handleCreate = async () => {
    const validation = validate();
    if (validation) {
      setMessage({ text: validation, tone: 'error' });
      return;
    }
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      const payload = {
        username: form.username.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        admin_privilege: form.adminPrivilege ? 1 : 0,
        chair_privilege: form.chairPrivilege ? 1 : 0,
        edit_privilege: form.editPrivilege ? 1 : 0,
        dark_mode: 0,
        activated: form.activated,
        ban: form.ban ? 1 : 0
      };
      const result = await apiFetch('/api/admins', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      setRefresh((value) => value + 1);
      resetForm();
      if (result?.id) {
        navigate(`/staff/admins/${result.id}`);
        return;
      }
      setMessage({ text: 'Admin created.', tone: 'info' });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to create admin.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const filters = (
    <input
      className="input table-search"
      type="search"
      placeholder="Search by name or email"
      value={query}
      onChange={(event) => {
        setQuery(event.target.value);
        setOffset(0);
      }}
    />
  );

  const pagination = {
    offset,
    limit,
    hasNext: items.length === limit,
    onPageChange: setOffset
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Admins</h1>
          <p className="subtle">Manage staff access and privileges.</p>
        </div>
        <Button variant="secondary" type="button" onClick={() => setShowForm((value) => !value)}>
          {showForm ? 'Hide Form' : 'Add Admin'}
        </Button>
      </div>

      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}

      {showForm ? (
        <Card title="New Admin">
          <div className="form-grid">
            <Input label="Username" value={form.username} onChange={updateField('username')} placeholder="advisor@calstatela.edu" />
            <Input label="First Name" value={form.firstName} onChange={updateField('firstName')} />
            <Input label="Last Name" value={form.lastName} onChange={updateField('lastName')} />
            <Input label="Email" value={form.email} onChange={updateField('email')} placeholder="advisor@calstatela.edu" />
          </div>
          <div className="toggle-row">
            <div>
              <div className="label">Admin Privilege</div>
              <div className="subtle">Full admin access to staff modules.</div>
            </div>
            <label className="switch">
              <input type="checkbox" checked={form.adminPrivilege} onChange={updateField('adminPrivilege')} />
              <span className="slider" />
            </label>
          </div>
          <div className="toggle-row">
            <div>
              <div className="label">Chair Privilege</div>
              <div className="subtle">Can finalize course change approvals.</div>
            </div>
            <label className="switch">
              <input type="checkbox" checked={form.chairPrivilege} onChange={updateField('chairPrivilege')} />
              <span className="slider" />
            </label>
          </div>
          <div className="toggle-row">
            <div>
              <div className="label">Edit Privilege</div>
              <div className="subtle">Can edit student plans and record advisor decisions.</div>
            </div>
            <label className="switch">
              <input type="checkbox" checked={form.editPrivilege} onChange={updateField('editPrivilege')} />
              <span className="slider" />
            </label>
          </div>
          <div className="toggle-row">
            <div>
              <div className="label">Activated</div>
              <div className="subtle">Enable login for this admin.</div>
            </div>
            <label className="switch">
              <input type="checkbox" checked={form.activated} onChange={updateField('activated')} />
              <span className="slider" />
            </label>
          </div>
          <div className="toggle-row">
            <div>
              <div className="label">Hold Access</div>
              <div className="subtle">Temporarily disable login access.</div>
            </div>
            <label className="switch">
              <input type="checkbox" checked={form.ban} onChange={updateField('ban')} />
              <span className="slider" />
            </label>
          </div>
          <div className="form-actions">
            <Button variant="secondary" type="button" onClick={resetForm}>Reset</Button>
            <Button type="button" disabled={saving} onClick={handleCreate}>{saving ? 'Saving...' : 'Create Admin'}</Button>
          </div>
        </Card>
      ) : null}

      {error ? <div className="alert error">{error}</div> : null}
      {loading ? (
        <p className="subtle">Loading...</p>
      ) : (
        <Table
          columns={columns}
          data={tableData}
          filters={filters}
          pagination={pagination}
          emptyMessage="No admins found."
        />
      )}
    </div>
  );
}

function fullName(admin) {
  return `${admin.first_name || ''} ${admin.last_name || ''}`.trim();
}

function roleLabel(admin) {
  if (admin.chair_privilege === 1) return 'Chair';
  if (admin.admin_privilege === 1) return 'Admin';
  if (admin.edit_privilege === 1) return 'Editor';
  return 'View Only';
}

function renderPrivileges(admin) {
  const badges = [];
  if (admin.chair_privilege === 1) badges.push(<Badge key="chair" tone="success">Chair</Badge>);
  if (admin.admin_privilege === 1) badges.push(<Badge key="admin" tone="info">Admin</Badge>);
  if (admin.edit_privilege === 1) badges.push(<Badge key="edit" tone="warning">Editor</Badge>);
  if (badges.length === 0) badges.push(<Badge key="view-only" tone="info">View Only</Badge>);
  return <div className="badge-row">{badges}</div>;
}
